/**
 * Store Registry Module
 * 
 * This module provides a central registry for reactive stores.
 * It allows components to access stores without creating circular dependencies.
 * 
 * The registry pattern is used to break circular dependencies between
 * store implementations and components that consume them. Instead of
 * directly importing stores, components can access them through the registry.
 * 
 * This approach follows the dependency inversion principle by having
 * both stores and components depend on abstractions rather than concrete
 * implementations.
 */

import type { IReactiveStore } from '../as-components';

/**
 * Registry for reactive stores
 * Maps store names to store instances
 * 
 * This is a singleton registry that holds all registered stores.
 * Stores are registered with a unique name and can be retrieved
 * by that name.
 */
const storeRegistry: Record<string, IReactiveStore> = {};

/**
 * Register a store with the registry
 * 
 * This function adds a store to the registry with the given name.
 * If a store with the same name already exists, it will be overwritten.
 * 
 * @param name Unique name for the store
 * @param store The store instance to register
 */
export function registerStore(name: string, store: IReactiveStore): void {
  storeRegistry[name] = store;
}

/**
 * Get a store from the registry
 * 
 * This function retrieves a store from the registry by its name.
 * If no store with the given name exists, undefined is returned.
 * 
 * @param name The name of the store to retrieve
 * @returns The store instance or undefined if not found
 */
export function getStore(name: string): IReactiveStore | undefined {
  return storeRegistry[name];
}

/**
 * Get the default store from the registry
 * 
 * This function retrieves the default store from the registry.
 * The default store is registered with the name 'default'.
 * If the default store is not registered, an error is thrown.
 * 
 * @returns The default store instance
 * @throws Error if the default store is not registered
 */
export function getDefaultStore(): IReactiveStore {
  const defaultStore = storeRegistry['default'];
  if (!defaultStore) {
    throw new Error('Default store not registered');
  }
  return defaultStore;
} 